import { authClient } from "@d12labs/auth/client";
import {
	createRootRoute,
	createRoute,
	createRouter,
	Outlet,
	redirect,
} from "@tanstack/react-router";
import { AppShell } from "@/components/layout/app-shell";
// @scaffold:account start
import { AccountPage } from "@/components/pages/account.page";
// @scaffold:account end
// @scaffold:ai-usage start
import { AiUsagePage } from "@/components/pages/ai-usage.page";
// @scaffold:ai-usage end
import { ErrorPage } from "@/components/pages/error.page";
import { ForgotPasswordPage } from "@/components/pages/forgot-password.page";
import { HomePage } from "@/components/pages/home.page";
// @scaffold:jobs start
import { JobsAdminPage } from "@/components/pages/jobs-admin.page";
// @scaffold:jobs end
import { LoginPage } from "@/components/pages/login.page";
import { NotFoundPage } from "@/components/pages/not-found.page";
import { ResetPasswordPage } from "@/components/pages/reset-password.page";
// @scaffold:settings-ui start
import { SettingsPage } from "@/components/pages/settings.page";
// @scaffold:settings-ui end
import { SignupPage } from "@/components/pages/signup.page";
// @scaffold:two-factor start
import { TwoFactorVerifyPage } from "@/components/pages/two-factor-verify.page";
// @scaffold:two-factor end
import { UsersPage } from "@/components/pages/users.page";
import { pluginRouteFactories } from "../plugins/registry";

const rootRoute = createRootRoute({
	component: () => <Outlet />,
	notFoundComponent: NotFoundPage,
	errorComponent: ErrorPage,
});

const loginRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/login",
	component: LoginPage,
});

const signupRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/signup",
	component: SignupPage,
});

const forgotPasswordRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/forgot-password",
	component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/reset-password",
	validateSearch: (search: Record<string, unknown>) => ({
		token: typeof search.token === "string" ? search.token : "",
	}),
	component: ResetPasswordPage,
});

// @scaffold:two-factor start
const twoFactorVerifyRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/two-factor-verify",
	component: TwoFactorVerifyPage,
});
// @scaffold:two-factor end

const protectedLayout = createRoute({
	getParentRoute: () => rootRoute,
	id: "_protected",
	beforeLoad: async () => {
		const { data } = await authClient.getSession();
		if (!data?.session) {
			throw redirect({ to: "/login" });
		}
	},
	component: AppShell,
});

// The app launcher, mounted at "/home" inside the protected shell. `/` is the
// public marketing page (static HTML). Post-login and the sidebar back control
// land here.
const homeRoute = createRoute({
	getParentRoute: () => protectedLayout,
	path: "/home",
	component: HomePage,
});

// Chassis-level protected routes (not plugins — they manage the Better Auth
// user model directly). Add new ones here if/when /settings, /billing etc.
// land in core rather than as plugins.
// @scaffold:account start
const accountRoute = createRoute({
	getParentRoute: () => protectedLayout,
	path: "/account",
	component: AccountPage,
});
// @scaffold:account end

// @scaffold:ai-usage start
// Admin-only operator view. The API enforces ADMIN_EMAILS (the page renders a
// "no access" state on 403); the sidebar only surfaces it to admins.
const aiUsageRoute = createRoute({
	getParentRoute: () => protectedLayout,
	path: "/admin/ai-usage",
	component: AiUsagePage,
});
// @scaffold:ai-usage end

// @scaffold:jobs start
// Admin-only jobs operator view (dead letters / stuck / retry). API enforces ADMIN_EMAILS.
const jobsAdminRoute = createRoute({
	getParentRoute: () => protectedLayout,
	path: "/admin/jobs",
	component: JobsAdminPage,
});
// @scaffold:jobs end

// @scaffold:settings-ui start
// Admin-only runtime settings editor. API enforces ADMIN_EMAILS.
const settingsRoute = createRoute({
	getParentRoute: () => protectedLayout,
	path: "/admin/settings",
	component: SettingsPage,
});
// @scaffold:settings-ui end

// User roster — available to every signed-in user (a "Users" page is always
// present because Auth is always installed). Not admin-gated.
const usersRoute = createRoute({
	getParentRoute: () => protectedLayout,
	path: "/users",
	component: UsersPage,
});

// All plugin routes are mounted inside the protected layout alongside the
// chassis routes above.
const pluginRoutes = Object.values(pluginRouteFactories).flatMap((factory) => {
	const result = factory(protectedLayout);
	return Array.isArray(result) ? result : [result];
});

const routeTree = rootRoute.addChildren([
	loginRoute,
	signupRoute,
	forgotPasswordRoute,
	resetPasswordRoute,
	// @scaffold:two-factor start
	twoFactorVerifyRoute,
	// @scaffold:two-factor end
	protectedLayout.addChildren([
		homeRoute,
		// @scaffold:account start
		accountRoute,
		// @scaffold:account end
		// @scaffold:ai-usage start
		aiUsageRoute,
		// @scaffold:ai-usage end
		// @scaffold:jobs start
		jobsAdminRoute,
		// @scaffold:jobs end
		// @scaffold:settings-ui start
		settingsRoute,
		// @scaffold:settings-ui end
		usersRoute,
		...pluginRoutes,
	]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}
